import { defineComponent as _defineComponent } from 'vue'
import { vModelCheckbox as _vModelCheckbox, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, renderSlot as _renderSlot, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-1971ca8a"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "w-[2%]"
}
const _hoisted_2 = ["onClick"]
const _hoisted_3 = {
  key: 0,
  class: "text-lg ml-4"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["checked", "onChange"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { key: 0 }
const _hoisted_8 = ["colspan"]

import type { AcReusableTableColumn } from '@/types'
import { ref, computed, watch, onMounted } from 'vue'

interface Props {
    columns: AcReusableTableColumn[]
    data: any[]
    isSelectable?: boolean
    sortDirection?: 'asc' | 'desc'
    sortColumn?: string
    filteredTerm?: string
    filterCols?: string[]
    /* eslint-disable */
    itemKey?: string // Key to identify unique items (default: 'id')
    rowClicked?: (item: any) => void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'acDataTable',
  props: {
    columns: {},
    data: {},
    isSelectable: { type: Boolean, default: false },
    sortDirection: { default: 'asc' },
    sortColumn: { default: '' },
    filteredTerm: { default: '' },
    filterCols: { default: () => [] },
    itemKey: { default: 'id' },
    rowClicked: {}
  },
  emits: ["columnSorted", "selectionChanged", "rowClicked"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Selection state
const selectedItems = ref<any[]>([])
const isAllSelected = ref(false)

// Computed properties
const filteredData = computed(() => {
    if (!props.filteredTerm || props.filterCols.length === 0) {
        return props.data
    }
    
    return props.data.filter(item => {
        return props.filterCols.some(colKey => {
            const value = getNestedValue(item, colKey)
            return String(value).toLowerCase().includes(props.filteredTerm.toLowerCase())
        })
    })
})

const sortedData = computed(() => {
    if (!props.sortColumn) {
        return filteredData.value
    }
    
    const sorted = [...filteredData.value].sort((a, b) => {
        const aValue = getNestedValue(a, props.sortColumn)
        const bValue = getNestedValue(b, props.sortColumn)
        
        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return aValue.localeCompare(bValue)
        }
        
        if (aValue < bValue) return -1
        if (aValue > bValue) return 1
        return 0
    })
    
    return props.sortDirection === 'desc' ? sorted.reverse() : sorted
})

// Helper function to get nested object values (e.g., 'client.name')
const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj)
}

// Selection methods
const handleItemSelection = (item: any, isSelected: boolean) => {
    const itemId = item[props.itemKey]
    
    if (isSelected) {
        if (!selectedItems.value.some(selected => selected[props.itemKey] === itemId)) {
            selectedItems.value.push(item)
        }
    } else {
        selectedItems.value = selectedItems.value.filter(selected => selected[props.itemKey] !== itemId)
    }
    
    updateSelectAllState()
    emit('selectionChanged', selectedItems.value)
}

const handleSelectAll = () => {
    if (isAllSelected.value) {
        selectedItems.value = [...sortedData.value]
    } else {
        selectedItems.value = []
    }
    
    emit('selectionChanged', selectedItems.value)
}

const updateSelectAllState = () => {
    const visibleItemsCount = sortedData.value.length
    const selectedVisibleItems = selectedItems.value.filter(selected => 
        sortedData.value.some(visible => visible[props.itemKey] === selected[props.itemKey])
    ).length
    
    isAllSelected.value = visibleItemsCount > 0 && selectedVisibleItems === visibleItemsCount
}

const isItemSelected = (item: any): boolean => {
    return selectedItems.value.some(selected => selected[props.itemKey] === item[props.itemKey])
}

// Sorting methods
const handleColumnSort = (column: AcReusableTableColumn) => {
    if (column.isSortable) {
        emit('columnSorted', column.key)
    }
}

const handleRowClick = (item: any) => {
    emit('rowClicked', item)
}

// Watch for changes in filtered data to update selection state
watch(sortedData, () => {
    updateSelectAllState()
}, { deep: true })

// Watch for external selection changes
watch(() => props.data, () => {
    // Reset selection when data changes
    selectedItems.value = []
    isAllSelected.value = false
    emit('selectionChanged', selectedItems.value)
}, { deep: true })

// Debug logging
onMounted(() => {
    console.log('AcDataTable mounted')
    console.log('Props columns:', props.columns)
    console.log('Props data:', props.data)
    console.log('Props sortColumn:', props.sortColumn)
    console.log('Props sortDirection:', props.sortDirection)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("table", {
    class: _normalizeClass(["alt-table", _ctx.$attrs.class])
  }, [
    _createElementVNode("thead", null, [
      _createElementVNode("tr", null, [
        (_ctx.isSelectable)
          ? (_openBlock(), _createElementBlock("th", _hoisted_1, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isAllSelected).value = $event)),
                onChange: handleSelectAll,
                type: "checkbox",
                class: "table-checkbox"
              }, null, 544), [
                [_vModelCheckbox, isAllSelected.value]
              ])
            ]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
          return (_openBlock(), _createElementBlock("th", {
            key: column.key,
            style: _normalizeStyle({ width: column.width }),
            class: _normalizeClass({ 'cursor-pointer select-none': column.isSortable }),
            onClick: ($event: any) => (handleColumnSort(column))
          }, [
            _createTextVNode(_toDisplayString(column.title) + " ", 1),
            (column.isSortable && _ctx.sortColumn === column.key)
              ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_ctx.sortDirection === 'asc' ? '▲' : '▼'), 1))
              : _createCommentVNode("", true)
          ], 14, _hoisted_2))
        }), 128))
      ])
    ]),
    _createElementVNode("tbody", null, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedData.value, (item) => {
        return (_openBlock(), _createElementBlock("tr", {
          key: item[_ctx.itemKey],
          class: "hover:bg-gray-200 cursor-pointer",
          onClick: ($event: any) => (handleRowClick(item))
        }, [
          (_ctx.isSelectable)
            ? (_openBlock(), _createElementBlock("td", {
                key: 0,
                onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createElementVNode("input", {
                  checked: isItemSelected(item),
                  onChange: ($event: any) => (handleItemSelection(item, ($event.target as HTMLInputElement).checked)),
                  type: "checkbox",
                  class: "table-checkbox"
                }, null, 40, _hoisted_5)
              ]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
            return (_openBlock(), _createElementBlock("td", {
              key: column.key,
              style: _normalizeStyle({ width: column.width }),
              class: _normalizeClass({ 'clickable-cell': !column.preventRowClick }),
              onClick: ($event: any) => (column.preventRowClick ? $event.stopPropagation() : null)
            }, [
              _renderSlot(_ctx.$slots, `cell-${column.key}`, {
                item: item,
                value: getNestedValue(item, column.key),
                column: column
              }, () => [
                _createTextVNode(_toDisplayString(getNestedValue(item, column.key)), 1)
              ])
            ], 14, _hoisted_6))
          }), 128))
        ], 8, _hoisted_4))
      }), 128)),
      (sortedData.value.length === 0)
        ? (_openBlock(), _createElementBlock("tr", _hoisted_7, [
            _createElementVNode("td", {
              colspan: _ctx.columns.length + (_ctx.isSelectable ? 1 : 0),
              class: "text-center py-8"
            }, [
              _renderSlot(_ctx.$slots, "empty-state", {}, () => [
                _createTextVNode(" No data available ")
              ])
            ], 8, _hoisted_8)
          ]))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})